import { useEffect } from "react"
import mixpanel from "mixpanel-browser"

const DEFAULT_HOST = "https://api-js.mixpanel.com"
const IS_PRODUCTION = process.env.NODE_ENV === "production"

// [TODO:TS] Placeholder type for event property values, will be replaced with the actual event property value type and properties once determined.
type EventPropertyValue = string | number | boolean | null | undefined
type MixpanelWithMeta = typeof mixpanel & { __loaded?: boolean }
const mixpanelInstance: MixpanelWithMeta = mixpanel as MixpanelWithMeta

export interface UseMixPanelOptions {
  distinctId: string
  eventProperties?: Record<string, EventPropertyValue>
  sessionSampleRate?: number
}

function readMixpanelToken(): string | null {
  const bundle = document.getElementById("app")?.getAttribute("data-bundle")
  if (!bundle) {
    return null
  }

  try {
    const data = JSON.parse(bundle) as {
      mixpanelToken?: string
    }

    return data.mixpanelToken ?? null
  } catch {
    return null
  }
}

export default function useMixPanel(options: UseMixPanelOptions): void {
  const {
    distinctId = null,
    eventProperties,
    sessionSampleRate = 100,
  } = options

  useEffect(() => {
    if (!IS_PRODUCTION || !distinctId) {
      return
    }

    const token = readMixpanelToken()
    if (!token) {
      return
    }

    if (mixpanelInstance.__loaded) {
      return
    }

    try {
      // IMPORTANT: When changing any of the parameters related to security, make sure to consult the team and ensure to consider best security practices.\
      // And example would be not to completely disable masking as it can review sensitive data.
      mixpanel.init(token, {
        api_host: DEFAULT_HOST,
        record_sessions_percent: sessionSampleRate,
        autocapture: {
          pageview: "full-url",
          click: true,
          input: true,
          scroll: true,
          submit: true,
          capture_text_content: false,
        },
      })
    } catch {
      // Silent fail - don't break app if Mixpanel fails
    }
  }, [sessionSampleRate, distinctId])

  useEffect(() => {
    if (!IS_PRODUCTION || !mixpanelInstance.__loaded || !distinctId) {
      return
    }

    try {
      mixpanel.register(eventProperties ?? {})
    } catch {
      // Silent fail - don't break app if Mixpanel fails
    }
  }, [eventProperties, distinctId])

  useEffect(() => {
    if (!IS_PRODUCTION || !mixpanelInstance.__loaded || !distinctId) {
      return
    }

    try {
      mixpanel.identify(distinctId)
    } catch {
      // Silent fail - don't break app if Mixpanel fails
    }
  }, [distinctId])

  useEffect(() => {
    if (!IS_PRODUCTION || !mixpanelInstance.__loaded || !distinctId) {
      return
    }

    try {
      mixpanel.opt_in_tracking()
      mixpanel.start_session_recording()
    } catch {
      // Silent fail - don't break app if Mixpanel fails
    }

    return () => {
      if (!IS_PRODUCTION || !mixpanelInstance.__loaded) {
        return
      }

      try {
        mixpanel.stop_session_recording()
        mixpanel.opt_out_tracking()
      } catch {
        // Silent fail - don't break app if Mixpanel fails
      }
    }
  }, [distinctId])
}
